import { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Minus } from 'lucide-react';
import Field from './Fields/Field';
import { useAuth } from '@/auth';
import { checkAllPermissions, PERMISSIONS } from '@/auth/permissions';

const PermissionModal = ({ isOpen, onClose, onAccept, value }) => {




    const [permissions, setPermissions] = useState(value);
    useEffect(() => {
        if (!isOpen) setPermissions(value);
    }, [isOpen])
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Permisos</DialogTitle>
                    <DialogDescription>
                        Configura los permisos de lectura y escritura para vendedores y compradores.
                    </DialogDescription>
                </DialogHeader>
                <div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Rol</TableHead>
                                <TableHead>Descargar</TableHead>
                                <TableHead>Subir</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow>
                                <TableCell>Vendedor</TableCell>
                                <TableCell>
                                    <Checkbox
                                        checked={permissions.vendedor_read}
                                        onCheckedChange={(checked) => setPermissions({
                                            ...permissions,
                                            vendedor_read: checked
                                        })}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Checkbox
                                        checked={permissions.vendedor_write}
                                        onCheckedChange={(checked) => setPermissions({
                                            ...permissions,
                                            vendedor_write: checked
                                        })}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Comprador</TableCell>
                                <TableCell>
                                    <Checkbox
                                        checked={permissions.comprador_read}
                                        onCheckedChange={(checked) => setPermissions({
                                            ...permissions,
                                            comprador_read: checked
                                        })}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Checkbox
                                        checked={permissions.comprador_write}
                                        onCheckedChange={(checked) => setPermissions({
                                            ...permissions,
                                            comprador_write: checked
                                        })}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} className="mr-3">
                        Cancelar
                    </Button>
                    <Button onClick={() => onAccept && onAccept(permissions)}>Aceptar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

const HelpComponent = ({ onOpen }) => {
    return <Button onClick={onOpen} size="sm">Permisos</Button>
}



const OtherFile = ({
    file,
    onDelete,
    onChange,
    canUpload = false,
    canView = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const { user } = useAuth();
    const userPermissions = user?.permissions || [];
    const roles = user?.roles || [];
    const canManageTramites = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_TRAMITES.name]);

    const [data, setData] = useState({
        fileId: file?.id || 0,
        name: file?.name,
        permissions: {
            comprador_read: file?.comprador_read,
            comprador_write: file?.comprador_write,
            vendedor_read: file?.vendedor_read,
            vendedor_write: file?.vendedor_write
        }
    });

    const [buttonVisible, setButtonVisible] = useState(canManageTramites);
    const [path] = useState(file?.path);
    const [value, setValue] = useState(null);
    const [fileDelete, setFileDelete] = useState(null);
    const { visible, readOnly } = useMemo(() => {
        const isComprador = roles.includes('comprador');
        const isVendedor = roles.includes('vendedor');

        if (canManageTramites || canUpload) {
            return {
                visible: true,
                readOnly: false
            }
        }

        if (canView) {
            return {
                visible: true,
                readOnly: true
            }
        }

        const visible = (isComprador && data.permissions.comprador_read) || (isVendedor && data.permissions.vendedor_read);
        const readOnly = (isComprador && !data.permissions.comprador_write) || (isVendedor && !data.permissions.vendedor_write);

        return {
            visible,
            readOnly
        }
    }, [data, canUpload, canView]);

    const helpText = useMemo(() => {
        let accionARealizar = ["tramitadora"];
        if (canUpload && !canManageTramites) accionARealizar.push("usuario autorizado");
        if (canView && !canUpload && !canManageTramites) accionARealizar = ["usuario con permisos de visualización"];
        if (data.permissions.vendedor_write) accionARealizar.push("vendedor");
        if (data.permissions.comprador_write) accionARealizar.push("comprador");
        return `Acción a realizar por ${accionARealizar.join(" o ")}`;
    }, [data, canUpload, canView, canManageTramites]);

    const handleChangeFile = ({ name, value }) => {
        if (name === 'file_delete' && value === true) {
            setFileDelete(file);
        }
        if (value instanceof File) {
            setValue(value);
        } else {
            setValue(null);
        }
    }

    const handleDeleteClick = () => {
        onDelete && onDelete();
    }

    const onClose = () => {
        setIsOpen(false);
    }

    const handleAcceptModal = (values) => {
        setData({
            ...data,
            permissions: values
        });
        onClose();
    }

    useEffect(() => {
        onChange && onChange({ ...data, file: value, fileDelete });
    }, [data, value, fileDelete]);

    return (
        <div>
            {buttonVisible && visible && <Button
                aria-label="Delete input"
                variant="ghost"
                size="sm"
                onClick={handleDeleteClick}
                className="rounded-full text-blue-300 p-1"
            >
                <Minus size={16} />
            </Button>}

            <div className="my-2">
                <Field
                    type="file"
                    name="file"
                    value={value}
                    path={path}
                    onChange={handleChangeFile}
                    helpComponent={buttonVisible ? <HelpComponent onOpen={() => setIsOpen(true)} /> : null}
                    helpText={helpText}
                    visible={visible}
                    label={data.name}
                    labelPlaceHolder={'Nombre de archivo'}
                    labelWritable={canManageTramites}
                    //@ts-ignore
                    labelOnChange={e => setData({
                        ...data,
                        name: e.target.value
                    })}
                    inline={true}
                    readOnly={readOnly}
                />
            </div>
            <PermissionModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                onAccept={handleAcceptModal}
                value={data.permissions}
            />
        </div>
    )
}

export default OtherFile;
